import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import AppIcon from "../components/AppIcon";
import SchoolBrand from "../components/SchoolBrand";
import StatusToast from "../components/StatusToast";
import "../styles/dashboard.css";

const money = (value) => `INR ${Number(value || 0).toLocaleString("en-IN")}`;
const toMs = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const addMonthsToMonthKey = (monthKey, delta = 0) => {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return monthKey || "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const dt = new Date(Date.UTC(year, month - 1, 1));
  dt.setUTCMonth(dt.getUTCMonth() + Number(delta || 0));
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const monthKeyToLabel = (monthKey) => {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return monthKey || "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const dt = new Date(Date.UTC(year, month - 1, 1));
  return dt.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getReadableParentError = (err, fallbackMessage) => {
  const message = String(err?.response?.data?.message || "").trim();
  if (!message) return fallbackMessage;

  const lowered = message.toLowerCase();
  if (lowered.includes("session")) {
    return "Session issue detected. Please re-login if this continues.";
  }
  if (lowered.includes("not authorized")) {
    return "You are not authorized for this action. Please log in again.";
  }
  if (lowered.includes("payment proof")) {
    return "Payment proof could not be processed. Please check the details and try again.";
  }
  if (lowered.includes("homework")) {
    return "Homework action could not be completed. Please try again.";
  }
  if (lowered.includes("report card")) {
    return "Report card could not be loaded right now. Please try again.";
  }

  return message;
};

const ParentDashboard = () => {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState({ totalFee: 0, paidFee: 0, dueFee: 0, paidMonths: 0, totalMonths: 0 });
  const [transactions, setTransactions] = useState([]);
  const [homework, setHomework] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [homeworkSubmissions, setHomeworkSubmissions] = useState([]);
  const [submittingHomework, setSubmittingHomework] = useState(false);
  const [homeworkSubmitForm, setHomeworkSubmitForm] = useState({ homeworkId: "", note: "", attachmentUrl: "" });
  const [reportCardRows, setReportCardRows] = useState([]);
  const [reportCardLoading, setReportCardLoading] = useState(false);
  const [examTimetable, setExamTimetable] = useState([]);
  const [hallTickets, setHallTickets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [markingNotificationRead, setMarkingNotificationRead] = useState(false);
  const [parentProfile, setParentProfile] = useState(null);
  const [seenMeta, setSeenMeta] = useState({ homework: 0, notices: 0, events: 0 });
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [proofForm, setProofForm] = useState({ feeType: "tuition", monthKey: "", monthsToCover: 1, txnId: "", screenshotUrl: "" });
  const [feesView, setFeesView] = useState("submit");
  const [annualView, setAnnualView] = useState("submit");
  const [admissionView, setAdmissionView] = useState("submit");
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState({ account: false, children: false });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [topUserOpen, setTopUserOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const topUserRef = useRef(null);
  const SCHOOL_UPI_ID = process.env.REACT_APP_SCHOOL_UPI_ID || "mpips@ucobank";
  const SCHOOL_PAYEE = process.env.REACT_APP_SCHOOL_PAYEE || "Maharana Pratap International Public School";

  const selectedStudent = useMemo(
    () => students.find((student) => String(student.id) === String(selectedStudentId)) || students[0] || null,
    [students, selectedStudentId]
  );
  const currentMonthKey = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const selectedStudentCurrentMonthFee = useMemo(() => {
    if (!selectedStudent) return null;
    return (selectedStudent.fees?.monthlyFees || []).find((month) => month.monthKey === currentMonthKey) || null;
  }, [selectedStudent, currentMonthKey]);
  const selectedStudentCurrentMonthTransportFee = useMemo(() => {
    if (!selectedStudent || !selectedStudent.transport?.enabled) return null;
    return (selectedStudent.transport?.monthlyFees || []).find((month) => month.monthKey === currentMonthKey) || null;
  }, [selectedStudent, currentMonthKey]);
  const selectedStudentAnnualFee = useMemo(() => {
    if (!selectedStudent) return null;
    const annualFee = selectedStudent.fees?.annualFee || {};
    const academicYear = annualFee.academicYear || selectedStudent.academicYear || "";
    if (!academicYear) return null;
    return {
      monthKey: academicYear,
      monthLabel: academicYear,
      amount: Number(annualFee.amount || 0),
      status: annualFee.status || "pending",
      dueDate: annualFee.dueDate || null,
      paidAt: annualFee.paidAt || null
    };
  }, [selectedStudent]);
  const selectedStudentAdmissionFee = useMemo(() => {
    if (!selectedStudent) return null;
    const admissionFee = selectedStudent.fees?.admissionFee || {};
    const academicYear = admissionFee.academicYear || selectedStudent.academicYear || "";
    if (!academicYear) return null;
    return {
      monthKey: academicYear,
      monthLabel: academicYear,
      amount: Number(admissionFee.amount || 0),
      status: admissionFee.status || "pending",
      dueDate: admissionFee.dueDate || null,
      paidAt: admissionFee.paidAt || null
    };
  }, [selectedStudent]);
  const selectedStudentMonths = useMemo(() => (
    proofForm.feeType === "annual"
      ? (selectedStudentAnnualFee ? [selectedStudentAnnualFee] : [])
      : proofForm.feeType === "admission"
      ? (selectedStudentAdmissionFee ? [selectedStudentAdmissionFee] : [])
      : proofForm.feeType === "transport"
      ? (selectedStudent?.transport?.monthlyFees || [])
      : (selectedStudent?.fees?.monthlyFees || [])
  ), [selectedStudent, selectedStudentAnnualFee, selectedStudentAdmissionFee, proofForm.feeType]);
  const selectedStudentPendingMonths = useMemo(
    () => selectedStudentMonths.filter((month) => month.status !== "paid"),
    [selectedStudentMonths]
  );
  const proofCoverageMonths = useMemo(() => {
    if (["annual", "admission"].includes(proofForm.feeType)) {
      const annualEntry = selectedStudentMonths[0];
      if (!annualEntry || annualEntry.status === "paid" || annualEntry.monthKey !== proofForm.monthKey) return [];
      return [annualEntry];
    }
    const monthsToCover = Math.max(1, Math.min(12, Number(proofForm.monthsToCover) || 1));
    const startIdx = selectedStudentMonths.findIndex((month) => month.monthKey === proofForm.monthKey);
    if (startIdx < 0) return [];
    const selected = [];
    for (let idx = startIdx; idx < selectedStudentMonths.length; idx += 1) {
      const month = selectedStudentMonths[idx];
      if (!month || month.status === "paid") continue;
      selected.push(month);
      if (selected.length >= monthsToCover) break;
    }
    return selected;
  }, [proofForm.feeType, proofForm.monthKey, proofForm.monthsToCover, selectedStudentMonths]);
  const proofExpectedAmount = useMemo(
    () => proofCoverageMonths.reduce((sum, month) => sum + Number(month.amount || 0), 0),
    [proofCoverageMonths]
  );
  const proofMissingCoverageCount = useMemo(() => {
    if (["annual", "admission"].includes(proofForm.feeType)) return 0;
    const target = Math.max(1, Math.min(12, Number(proofForm.monthsToCover) || 1));
    return Math.max(0, target - proofCoverageMonths.length);
  }, [proofForm.feeType, proofForm.monthsToCover, proofCoverageMonths.length]);
  const proofFallbackMonthAmount = useMemo(() => {
    if (proofCoverageMonths.length > 0) return Number(proofCoverageMonths[0].amount || 0);
    if (proofForm.monthKey) {
      const selected = selectedStudentMonths.find((month) => month.monthKey === proofForm.monthKey);
      if (selected) return Number(selected.amount || 0);
    }
    return Number(selectedStudentMonths[0]?.amount || 0);
  }, [proofCoverageMonths, proofForm.monthKey, selectedStudentMonths]);
  const proofExpectedAmountWithAdvance = useMemo(
    () => proofExpectedAmount + (proofMissingCoverageCount * proofFallbackMonthAmount),
    [proofExpectedAmount, proofMissingCoverageCount, proofFallbackMonthAmount]
  );
  const proofCoverageRangeText = useMemo(() => {
    if (!proofForm.monthKey) return "Select start month";
    if (proofForm.feeType === "annual") return `${proofForm.monthKey} (Annual Fee)`;
    if (proofForm.feeType === "admission") return `${proofForm.monthKey} (Admission Fee)`;
    const target = Math.max(1, Math.min(12, Number(proofForm.monthsToCover) || 1));
    const startKey = proofCoverageMonths[0]?.monthKey || proofForm.monthKey;
    const endFromExisting = proofCoverageMonths[proofCoverageMonths.length - 1]?.monthKey || startKey;
    const endKey = proofMissingCoverageCount > 0
      ? addMonthsToMonthKey(endFromExisting, proofMissingCoverageCount)
      : endFromExisting;
    return `${monthKeyToLabel(startKey)} to ${monthKeyToLabel(endKey)} (${target} month${target > 1 ? "s" : ""})`;
  }, [proofForm.feeType, proofForm.monthKey, proofForm.monthsToCover, proofCoverageMonths, proofMissingCoverageCount]);
  const selectedStudentHomework = useMemo(() => {
    if (!selectedStudent) return [];
    return homework.filter((item) =>
      (item.linkedStudents || []).some((student) => String(student.id) === String(selectedStudent.id))
    );
  }, [homework, selectedStudent]);
  const selectedStudentSubmissions = useMemo(() => {
    if (!selectedStudent) return [];
    return homeworkSubmissions.filter(
      (item) => String(item.student?._id || item.student || "") === String(selectedStudent.id)
    );
  }, [homeworkSubmissions, selectedStudent]);
  const selectedStudentHallTickets = useMemo(() => {
    if (!selectedStudent) return [];
    return hallTickets.filter((item) => String(item.student?._id || item.student?.id || item.student) === String(selectedStudent.id));
  }, [hallTickets, selectedStudent]);

  const storageKeys = useMemo(() => {
    const keyBase = user?.id || user?.email || "parent";
    return {
      homework: `seen_homework_${keyBase}`,
      notices: `seen_notices_${keyBase}`,
      events: `seen_events_${keyBase}`
    };
  }, [user]);

  const latestHomeworkMs = useMemo(
    () => homework.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [homework]
  );
  const latestNoticeMs = useMemo(
    () => notices.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [notices]
  );
  const latestEventMs = useMemo(
    () => events.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [events]
  );

  const unreadHomeworkCount = useMemo(
    () => homework.filter((item) => toMs(item.createdAt) > (seenMeta.homework || 0)).length,
    [homework, seenMeta.homework]
  );
  const unreadNoticeCount = useMemo(
    () => notices.filter((item) => toMs(item.createdAt) > (seenMeta.notices || 0)).length,
    [notices, seenMeta.notices]
  );
  const unreadEventCount = useMemo(
    () => events.filter((item) => toMs(item.createdAt) > (seenMeta.events || 0)).length,
    [events, seenMeta.events]
  );

  const markSeen = (type) => {
    const seenAt = type === "homework"
      ? Math.max(Date.now(), latestHomeworkMs)
      : type === "events"
        ? Math.max(Date.now(), latestEventMs)
        : Math.max(Date.now(), latestNoticeMs);
    const key = type === "homework" ? storageKeys.homework : type === "events" ? storageKeys.events : storageKeys.notices;
    setSeenMeta((prev) => ({ ...prev, [type]: seenAt }));
    localStorage.setItem(key, String(seenAt));
  };

  const refreshDashboard = async () => {
    const [statusRes, txRes, homeworkRes, noticeRes, eventRes, proofRes, timetableRes, submissionsRes, notifRes, profileRes] = await Promise.all([
      api.get("/fees/status"),
      api.get("/fees/transactions"),
      api.get("/portal/parent/homework"),
      api.get("/portal/parent/notices"),
      api.get("/portal/parent/events"),
      api.get("/fees/proofs/self"),
      selectedStudentId ? api.get(`/portal/parent/timetable?studentId=${selectedStudentId}`).catch(() => ({ data: { entries: [] } })) : Promise.resolve({ data: { entries: [] } }),
      api.get("/portal/parent/homework-submissions").catch(() => ({ data: { submissions: [] } })),
      api.get("/portal/notifications").catch(() => ({ data: { notifications: [], unreadCount: 0 } })),
      api.get("/portal/parent/profile").catch(() => ({ data: { parent: null } }))
    ]);
    const incomingStudents = statusRes.data.students || [];
    setStudents(incomingStudents);
    setSummary(statusRes.data.summary || { totalFee: 0, paidFee: 0, dueFee: 0, paidMonths: 0, totalMonths: 0 });
    setTransactions(txRes.data.transactions || []);
    setHomework(homeworkRes.data.homework || []);
    setNotices(noticeRes.data.notices || []);
    setEvents(eventRes.data.events || []);
    setProofs(proofRes.data.proofs || []);
    setTimetable(timetableRes.data.entries || []);
    setHomeworkSubmissions(submissionsRes.data.submissions || []);
    setNotifications(notifRes.data.notifications || []);
    setNotificationUnreadCount(Number(notifRes.data.unreadCount || 0));
    setParentProfile(profileRes.data.parent || null);
    setSelectedStudentId((prev) => {
      if (incomingStudents.length === 0) return "";
      if (prev && incomingStudents.some((student) => String(student.id) === String(prev))) return prev;
      return String(incomingStudents[0].id);
    });
  };

  const handleProofScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image screenshot.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Screenshot size should be less than 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProofForm((prev) => ({ ...prev, screenshotUrl: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const submitProof = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    if (proofForm.feeType === "transport" && !selectedStudent.transport?.enabled) {
      setError("Transport fee is not enabled for this student.");
      return;
    }
    if (!proofForm.monthKey || !proofForm.screenshotUrl) {
      setError("Please select month and upload screenshot.");
      return;
    }
    const monthsToCover = ["annual", "admission"].includes(proofForm.feeType)
      ? 1
      : Math.max(1, Math.min(12, Number(proofForm.monthsToCover) || 1));
    setProofSubmitting(true);
    setError("");
    try {
      await api.post("/fees/proofs", {
        studentId: selectedStudent.id,
        feeType: proofForm.feeType,
        monthKey: proofForm.monthKey,
        monthsToCover,
        txnId: proofForm.txnId,
        amount: proofExpectedAmountWithAdvance,
        screenshotUrl: proofForm.screenshotUrl
      });
      setProofForm((prev) => ({ ...prev, monthKey: "", monthsToCover: 1, txnId: "", screenshotUrl: "" }));
      setSuccess("Payment proof submitted. Awaiting admin approval.");
    } catch (err) {
      setError(getReadableParentError(err, "Failed to submit payment proof"));
    } finally {
      setProofSubmitting(false);
    }
  };

  useEffect(() => {
    if (proofForm.feeType === "transport" && !selectedStudent?.transport?.enabled) {
      setProofForm((prev) => ({ ...prev, feeType: "tuition", monthKey: "" }));
    }
  }, [selectedStudent, proofForm.feeType]);

  useEffect(() => {
    if (activeTab === "annual-fee") {
      setProofForm((prev) => ({
        ...prev,
        feeType: "annual",
        monthKey: selectedStudentAnnualFee?.monthKey || prev.monthKey || "",
        monthsToCover: 1
      }));
      return;
    }
    if (activeTab === "admission-fee") {
      setProofForm((prev) => ({
        ...prev,
        feeType: "admission",
        monthKey: selectedStudentAdmissionFee?.monthKey || prev.monthKey || "",
        monthsToCover: 1
      }));
      return;
    }
    if (activeTab === "fees" && ["annual", "admission"].includes(proofForm.feeType)) {
      setProofForm((prev) => ({ ...prev, feeType: "tuition", monthKey: "" }));
    }
  }, [activeTab, proofForm.feeType, selectedStudentAnnualFee, selectedStudentAdmissionFee]);

  const selectedStudentProofs = useMemo(() => {
    if (!selectedStudent) return [];
    return proofs.filter((proof) =>
      String(proof.student?._id || proof.student) === String(selectedStudent.id)
      && String(proof.feeType || "tuition") === String(proofForm.feeType || "tuition")
    );
  }, [proofs, selectedStudent, proofForm.feeType]);
  const selectedStudentProofCounts = useMemo(() => {
    return selectedStudentProofs.reduce((acc, proof) => {
      const key = proof.status || "pending";
      acc[key] = Number(acc[key] || 0) + 1;
      return acc;
    }, { pending: 0, approved: 0, rejected: 0 });
  }, [selectedStudentProofs]);
  const selectedStudentAnnualProofs = useMemo(() => {
    if (!selectedStudent) return [];
    return proofs.filter((proof) =>
      String(proof.student?._id || proof.student) === String(selectedStudent.id)
      && String(proof.feeType || "tuition") === "annual"
    );
  }, [proofs, selectedStudent]);
  const selectedStudentAnnualProofCounts = useMemo(() => {
    return selectedStudentAnnualProofs.reduce((acc, proof) => {
      const key = proof.status || "pending";
      acc[key] = Number(acc[key] || 0) + 1;
      return acc;
    }, { pending: 0, approved: 0, rejected: 0 });
  }, [selectedStudentAnnualProofs]);

  const selectedStudentAdmissionProofs = useMemo(() => {
    if (!selectedStudent) return [];
    return proofs.filter((proof) =>
      String(proof.student?._id || proof.student) === String(selectedStudent.id)
      && String(proof.feeType || "tuition") === "admission"
    );
  }, [proofs, selectedStudent]);
  const selectedStudentAdmissionProofCounts = useMemo(() => {
    return selectedStudentAdmissionProofs.reduce((acc, proof) => {
      const key = proof.status || "pending";
      acc[key] = Number(acc[key] || 0) + 1;
      return acc;
    }, { pending: 0, approved: 0, rejected: 0 });
  }, [selectedStudentAdmissionProofs]);

  const loadReportCard = async () => {
    if (!selectedStudent?.id) {
      setReportCardRows([]);
      return;
    }
    setReportCardLoading(true);
    try {
      const { data } = await api.get(`/portal/parent/report-card?studentId=${selectedStudent.id}`);
      setReportCardRows(data.report || []);
    } catch (err) {
      setError(getReadableParentError(err, "Failed to load report card"));
    } finally {
      setReportCardLoading(false);
    }
  };

  const loadParentExtras = async (studentId) => {
    if (!studentId) {
      setTimetable([]);
      setExamTimetable([]);
      setHallTickets([]);
      return;
    }
    try {
      const [ttRes, subRes, examRes, ticketRes] = await Promise.all([
        api.get(`/portal/parent/timetable?studentId=${studentId}`),
        api.get("/portal/parent/homework-submissions"),
        api.get(`/portal/parent/exam-timetable?studentId=${studentId}`),
        api.get(`/portal/parent/hall-tickets?studentId=${studentId}`)
      ]);
      setTimetable(ttRes.data.entries || []);
      setHomeworkSubmissions(subRes.data.submissions || []);
      setExamTimetable(examRes.data.exams || []);
      setHallTickets(ticketRes.data.tickets || []);
    } catch {
      setTimetable([]);
      setExamTimetable([]);
      setHallTickets([]);
    }
  };

  const startResubmit = (proof) => {
    setProofForm({
      feeType: proof.feeType || "tuition",
      monthKey: proof.monthKey || "",
      monthsToCover: ["annual", "admission"].includes(proof.feeType) ? 1 : Number(proof.monthsToCover || 1),
      txnId: "",
      screenshotUrl: ""
    });
    setActiveTab(proof.feeType === "annual" ? "annual-fee" : proof.feeType === "admission" ? "admission-fee" : "fees");
  };

  const handleHomeworkAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      setError("Please upload an image or PDF.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setError("Attachment size should be less than 3MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setHomeworkSubmitForm((prev) => ({ ...prev, attachmentUrl: String(reader.result || "") }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const submitHomework = async (e) => {
    e.preventDefault();
    if (!selectedStudent?.id || !homeworkSubmitForm.homeworkId) {
      setError("Please select homework first.");
      return;
    }
    setSubmittingHomework(true);
    setError("");
    try {
      await api.post("/portal/parent/homework-submissions", {
        homeworkId: homeworkSubmitForm.homeworkId,
        studentId: selectedStudent.id,
        note: homeworkSubmitForm.note,
        attachmentUrl: homeworkSubmitForm.attachmentUrl
      });
      setHomeworkSubmitForm({ homeworkId: "", note: "", attachmentUrl: "" });
      await loadParentExtras(selectedStudent.id);
      setSuccess("Homework submitted successfully.");
    } catch (err) {
      setError(getReadableParentError(err, "Failed to submit homework"));
    } finally {
      setSubmittingHomework(false);
    }
  };

  const buildHallTicketHtml = (ticket) => {
    const studentName = escapeHtml(ticket.student?.name || selectedStudent?.name || "Student");
    const className = escapeHtml(ticket.student?.class || selectedStudent?.class || "-");
    const section = escapeHtml(ticket.student?.section || selectedStudent?.section || "-");
    const rollNo = escapeHtml(ticket.student?.rollNo || selectedStudent?.rollNo || "-");
    const admissionNo = escapeHtml(ticket.student?.admissionNo || selectedStudent?.admissionNo || "-");
    const examTitle = escapeHtml(ticket.exam?.title || "-");
    const subject = escapeHtml(ticket.exam?.subject || "-");
    const examDate = ticket.exam?.examDate ? new Date(ticket.exam.examDate).toLocaleDateString() : "-";
    const centerName = escapeHtml(ticket.centerName || "-");
    const room = escapeHtml(ticket.room || "-");
    const seatNumber = escapeHtml(ticket.seatNumber || "-");
    const instructions = escapeHtml(ticket.instructions || "Carry school ID card and arrive 30 minutes early.");
    return `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Hall Ticket - ${examTitle}</title>
        <style>
          body { font-family: "Segoe UI", sans-serif; background:#f5f7fb; margin:0; padding:24px; color:#0f1e35; }
          .ticket { max-width:780px; margin:0 auto; background:#fff; border:1px solid #dbe3ef; border-radius:16px; overflow:hidden; }
          .head { background: linear-gradient(135deg,#1a3d73,#295ba9); color:#fff; padding:18px 22px; }
          .head h1 { margin:0; font-size:22px; font-weight:500; }
          .head p { margin:6px 0 0; opacity:.92; }
          .grid { display:grid; grid-template-columns:1fr 1fr; gap:12px 20px; padding:22px; }
          .item b { display:block; font-size:12px; color:#64748b; margin-bottom:4px; font-weight:500; letter-spacing:.2px; }
          .item span { font-size:15px; font-weight:500; color:#10223c; }
          .instructions { margin:0 22px 18px; border:1px dashed #c9d6eb; border-radius:12px; padding:14px; background:#f8fbff; }
          .instructions b { display:block; margin-bottom:6px; font-weight:500; }
          .foot { display:flex; justify-content:space-between; align-items:flex-end; padding:18px 22px 24px; }
          .sig { text-align:right; min-width:220px; }
          .sig-line { margin-top:34px; border-top:1px solid #263b5f; padding-top:6px; font-size:12px; color:#64748b; }
          @media print {
            body { background:#fff; padding:0; }
            .ticket { border:none; border-radius:0; }
          }
        </style>
      </head>
      <body>
        <article class="ticket">
          <header class="head">
            <h1>MPIPS - Exam Hall Ticket</h1>
            <p>Academic Year: ${escapeHtml(ticket.exam?.academicYear || selectedStudent?.academicYear || "-")}</p>
          </header>
          <section class="grid">
            <div class="item"><b>Student Name</b><span>${studentName}</span></div>
            <div class="item"><b>Class / Section</b><span>${className} - ${section}</span></div>
            <div class="item"><b>Roll Number</b><span>${rollNo}</span></div>
            <div class="item"><b>Admission Number</b><span>${admissionNo}</span></div>
            <div class="item"><b>Exam</b><span>${examTitle}</span></div>
            <div class="item"><b>Subject</b><span>${subject}</span></div>
            <div class="item"><b>Exam Date</b><span>${escapeHtml(examDate)}</span></div>
            <div class="item"><b>Seat Number</b><span>${seatNumber}</span></div>
            <div class="item"><b>Room</b><span>${room}</span></div>
            <div class="item"><b>Center</b><span>${centerName}</span></div>
          </section>
          <section class="instructions">
            <b>Instructions</b>
            <span>${instructions}</span>
          </section>
          <footer class="foot">
            <small>Generated on ${new Date().toLocaleString()}</small>
            <div class="sig">
              <div class="sig-line">Principal / Authorized Signatory</div>
            </div>
          </footer>
        </article>
      </body>
      </html>
    `;
  };

  const openHallTicketDocument = (ticket, autoPrint = false) => {
    const html = buildHallTicketHtml(ticket);
    const win = window.open("", "_blank", "width=900,height=980");
    if (!win) {
      setError("Popup blocked. Please allow popups to print/download hall ticket.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    if (autoPrint) {
      win.onload = () => {
        win.focus();
        win.print();
      };
    }
  };

  const markNotificationsRead = async () => {
    setMarkingNotificationRead(true);
    setError("");
    try {
      await api.post("/portal/notifications/mark-read");
      setNotifications((prev) => prev.map((item) => ({ ...item, unread: false })));
      setNotificationUnreadCount(0);
    } catch (err) {
      setError(getReadableParentError(err, "Failed to mark notifications as read"));
    } finally {
      setMarkingNotificationRead(false);
    }
  };

  useEffect(() => {
    const homeworkSeen = Number(localStorage.getItem(storageKeys.homework) || 0);
    const noticesSeen = Number(localStorage.getItem(storageKeys.notices) || 0);
    const eventsSeen = Number(localStorage.getItem(storageKeys.events) || 0);
    setSeenMeta({ homework: homeworkSeen, notices: noticesSeen, events: eventsSeen });
  }, [storageKeys]);

  useEffect(() => {
    if (!error && !success) return undefined;
    const timer = setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  useEffect(() => {
    if (activeTab === "homework") markSeen("homework");
    if (activeTab === "notices") markSeen("notices");
    if (activeTab === "events") markSeen("events");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!selectedStudentId) return;
    if (!["timetable", "homework-submit", "hall-ticket"].includes(activeTab)) return;
    loadParentExtras(selectedStudentId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId, activeTab]);

  useEffect(() => {
    if (activeTab !== "report-card") return;
    loadReportCard().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedStudentId]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeTab, selectedStudentId]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!topUserRef.current) return;
      if (!topUserRef.current.contains(event.target)) {
        setTopUserOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const logoutToParentLogin = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("teacherToken");
    localStorage.removeItem("parentToken");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const copyToClipboard = async (value, label) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      setError(`${label} not available to copy.`);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallback = document.createElement("textarea");
        fallback.value = text;
        fallback.setAttribute("readonly", "");
        fallback.style.position = "absolute";
        fallback.style.left = "-9999px";
        document.body.appendChild(fallback);
        fallback.select();
        document.execCommand("copy");
        document.body.removeChild(fallback);
      }
      setSuccess(`${label} copied.`);
    } catch {
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const copySchoolUpiId = async (contextLabel = "UPI ID") => {
    await copyToClipboard(SCHOOL_UPI_ID, contextLabel);
  };

  useEffect(() => {
    let token = localStorage.getItem("parentToken");
    if (!token) {
      const fallback = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      if (fallback && role === "parent") {
        token = fallback;
        localStorage.setItem("parentToken", fallback);
      }
    }
    if (!token) return navigate("/login");

    let initialLoaded = false;
    let polling = false;
    const load = async () => {
      if (polling) return;
      polling = true;
      const isInitialRequest = !initialLoaded;
      if (isInitialRequest) setLoading(true);
      setError("");
      try {
        await refreshDashboard();
        initialLoaded = true;
      } catch (err) {
        if ([401, 403].includes(err.response?.status)) {
          setError("Session issue detected. Please re-login if this continues.");
        } else {
          setError(getReadableParentError(err, "Failed to load dashboard"));
        }
      } finally {
        if (isInitialRequest) setLoading(false);
        polling = false;
      }
    };

    load();
    const timer = setInterval(() => load(), 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) return <div className="dash-root"><p>Loading dashboard...</p></div>;

  const parentName = parentProfile?.name || user?.name || "Parent";
  const parentRole = "Parent";
  const parentPassword = parentProfile?.loginPassword || "";
  const parentInitials = parentName
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const setTabAndCloseMenu = (tab) => {
    setActiveTab(tab);
    setMobileNavOpen(false);
  };

  return (
    <div className={`dash-root parent-mobile-root parent-portal ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <StatusToast
        message={error || success}
        type={error ? "error" : "success"}
        onClose={() => {
          setError("");
          setSuccess("");
        }}
      />
      {profileModalOpen && (
        <div className="confirm-modal-backdrop" role="presentation">
          <div className="confirm-modal-card profile-modal-card" role="dialog" aria-modal="true" aria-label="Parent profile">
            <button type="button" className="profile-modal-close" onClick={() => setProfileModalOpen(false)} aria-label="Close profile">
              <AppIcon name="close" />
            </button>
            <div className="profile-modal-head">
              <span className="profile-head-avatar">{parentInitials || "PR"}</span>
              <div className="profile-head-meta">
                <h3>Parent Profile</h3>
                <span className="profile-role-badge">{parentRole}</span>
              </div>
            </div>
            <div className="profile-grid">
              <div className="profile-row"><strong>Name:</strong><span>{parentName}</span></div>
              <div className="profile-row">
                <strong>Email:</strong>
                <span className="profile-value-wrap">
                  <span>{parentProfile?.email || user?.email || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(parentProfile?.email || user?.email, "Email")} title="Copy email">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Role:</strong><span>{parentRole}</span></div>
              <div className="profile-row">
                <strong>Phone:</strong>
                <span className="profile-value-wrap">
                  <span>{parentProfile?.phone || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(parentProfile?.phone, "Phone")} title="Copy phone">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Address:</strong><span>{parentProfile?.address || "-"}</span></div>
              <div className="profile-row profile-password-row">
                <strong>Password:</strong>
                <span className="profile-value-wrap">
                  <span>{showProfilePassword ? (parentPassword || "Not set") : "********"}</span>
                <button
                  type="button"
                  className="profile-copy-btn"
                  onClick={() => setShowProfilePassword((prev) => !prev)}
                  title={showProfilePassword ? "Hide password" : "Show password"}
                >
                  <AppIcon name={showProfilePassword ? "eyeOff" : "eye"} />
                </button>
                </span>
              </div>
            </div>
            <div className="confirm-modal-actions">
              <button type="button" onClick={() => setProfileModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <button
        type="button"
        className={mobileNavOpen ? "mobile-nav-overlay show" : "mobile-nav-overlay"}
        onClick={() => setMobileNavOpen(false)}
        aria-label="Close menu"
      />
      <aside className={`dash-sidebar ${mobileNavOpen ? "mobile-open" : ""}`}>
        <SchoolBrand />
        <div className="dash-sidebar-inner">
          <nav className="dash-nav">
            <div className="dash-nav-group">
              <button className={activeTab === "overview" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("overview")}>
                <span className="nav-row"><AppIcon name="dashboard" className="nav-icon" /><span className="nav-text">Dashboard</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, account: !prev.account }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="account" className="nav-icon" /><span className="nav-text">Fee Account</span></span>
                  <AppIcon name="chevronRight" className={sidebarOpen.account ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.account ? "dash-submenu open" : "dash-submenu"}>
                  <button className={activeTab === "fees" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setTabAndCloseMenu("fees")}>
                    <span className="nav-row"><AppIcon name="fee" className="nav-icon" /><span className="nav-text">Pay Fees</span></span>
                  </button>
                  <button className={activeTab === "annual-fee" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setTabAndCloseMenu("annual-fee")}>
                    <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Annual Fee</span></span>
                  </button>
                  <button className={activeTab === "admission-fee" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setTabAndCloseMenu("admission-fee")}>
                    <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Admission Fee</span></span>
                  </button>
                  <button className={activeTab === "history" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setTabAndCloseMenu("history")}>
                    <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Fee Payment History</span></span>
                  </button>
              </div>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "homework" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("homework")}>
                <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Homework</span>{unreadHomeworkCount > 0 && <span className="dash-badge">{unreadHomeworkCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "homework-submit" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("homework-submit")}>
                <span className="nav-row"><AppIcon name="check" className="nav-icon" /><span className="nav-text">Homework Submission</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "timetable" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("timetable")}>
                <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">Class Timetable</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "report-card" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("report-card")}>
                <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">Report Card</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "hall-ticket" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("hall-ticket")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Exam Hall Ticket</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "notices" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("notices")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Notice Board</span>{unreadNoticeCount > 0 && <span className="dash-badge">{unreadNoticeCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "events" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("events")}>
                <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">School Events</span>{unreadEventCount > 0 && <span className="dash-badge">{unreadEventCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "notifications" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setTabAndCloseMenu("notifications")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Notifications</span>{notificationUnreadCount > 0 && <span className="dash-badge">{notificationUnreadCount}</span>}</span>
              </button>
            </div>
          </nav>

          <div className="dash-children-list">
            <p className="dash-list-title">Children</p>
            {students.length === 0 && <p className="dash-empty">No students linked yet.</p>}
            {students.map((student) => (
              <button key={student.id} className={String(selectedStudent?.id) === String(student.id) ? "dash-child-btn active" : "dash-child-btn"} onClick={() => setSelectedStudentId(String(student.id))}>
                <span className="child-btn-name-row">
                  {student.photoUrl ? (
                    <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                  ) : (
                    <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                  )}
                  <span>{student.name}</span>
                </span>
                <small>{student.class}-{student.section || "NA"}</small>
              </button>
            ))}
          </div>
        </div>
        <button className="dash-logout" onClick={logoutToParentLogin}>
          <span className="nav-row"><AppIcon name="logout" className="nav-icon" /><span className="nav-text">Logout</span></span>
        </button>
      </aside>

      <section className="dash-main">
        <header className="dash-topbar">
          <button type="button" className="mobile-nav-toggle" onClick={() => setMobileNavOpen((prev) => !prev)} aria-label="Toggle menu">
            <AppIcon name="list" size={16} />
          </button>
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <AppIcon name="chevronRight" className={sidebarCollapsed ? "collapse-icon collapsed" : "collapse-icon"} />
          </button>
          <h1 className="dash-top-title admin-top-title">Welcome To MPIPS School Management System Parent Dashboard</h1>
          <div className="top-user-wrap" ref={topUserRef}>
            <button type="button" className="top-user-btn" onClick={() => setTopUserOpen((prev) => !prev)}>
              <span className="top-user-avatar">{parentInitials || "PR"}</span>
              <span className="top-user-meta">
                <strong>{parentName}</strong>
                <small>{parentRole}</small>
              </span>
              <AppIcon name="chevronRight" className={topUserOpen ? "submenu-arrow open" : "submenu-arrow"} />
            </button>
            {topUserOpen && (
              <div className="top-user-dropdown">
                <button type="button" onClick={() => { setProfileModalOpen(true); setTopUserOpen(false); }}>View Profile</button>
                <button type="button" onClick={logoutToParentLogin}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <main className="dash-content">
          <p className="dash-breadcrumb">Home - Parent</p>
          <div className="mobile-only mobile-children-switch">
            {students.map((student) => (
              <button
                key={student.id}
                type="button"
                className={String(selectedStudent?.id) === String(student.id) ? "mobile-child-pill active" : "mobile-child-pill"}
                onClick={() => setSelectedStudentId(String(student.id))}
              >
                {student.name}
              </button>
            ))}
          </div>
          {activeTab === "overview" && (
            <section>
              <div className="portal-hero">
                <div>
                  <h2>Parent Dashboard</h2>
                  <p>Track fees, homework, notices, and events for your selected child in one place.</p>
                </div>
                <div className="portal-hero-badges">
                  <span className="hero-badge">Children: {students.length}</span>
                  <span className="hero-badge">Current Month Fee: {money(selectedStudentCurrentMonthFee?.amount || 0)}</span>
                </div>
              </div>
              <div className="dash-panel">
                <h3>Month Completion</h3>
                <p>Paid Months: <strong>{summary.paidMonths || 0}</strong> / {summary.totalMonths || 0}</p>
              </div>
              {selectedStudent && (
                <div className="dash-panel" style={{ marginTop: 12 }}>
                  <div className="profile-header-row">
                    {selectedStudent.photoUrl ? (
                      <img src={selectedStudent.photoUrl} alt={selectedStudent.name} className="profile-avatar" />
                    ) : (
                      <div className="profile-avatar placeholder"><AppIcon name="child" size={24} /></div>
                    )}
                    <div>
                      <h3>{selectedStudent.name}</h3>
                      <p>Class {selectedStudent.class}-{selectedStudent.section || "NA"} | Roll {selectedStudent.rollNo || "NA"}</p>
                    </div>
                  </div>
                  <p>Academic Year: {selectedStudent.academicYear || "NA"}</p>
                  <hr style={{ border: 0, borderTop: "1px solid #dde3ee", margin: "10px 0" }} />
                  <p style={{ marginBottom: 6 }}>
                    Current Month Fee ({selectedStudentCurrentMonthFee?.monthLabel || currentMonthKey}):{" "}
                    <strong>{money(selectedStudentCurrentMonthFee?.amount || 0)}</strong>
                  </p>
                  <p style={{ marginTop: 0 }}>
                    Status:{" "}
                    <span className={selectedStudentCurrentMonthFee?.status === "paid" ? "status-pill paid" : "status-pill due"}>
                      {selectedStudentCurrentMonthFee?.status === "paid" ? "Paid" : "Pending"}
                    </span>
                  </p>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Copy the school UPI ID, pay in any UPI app, then upload the screenshot in Fee Collection.
                  </p>
                  {selectedStudentCurrentMonthFee?.status === "paid" ? (
                    <button className="table-btn" type="button" disabled>Paid</button>
                  ) : (
                    <button
                      className="table-btn"
                      type="button"
                      onClick={() => copySchoolUpiId("Tuition UPI ID")}
                      disabled={!selectedStudentCurrentMonthFee}
                    >
                      Copy Tuition UPI ID
                    </button>
                  )}
                  {selectedStudent?.transport?.enabled && (
                    <>
                      <hr style={{ border: 0, borderTop: "1px solid #dde3ee", margin: "10px 0" }} />
                      <p style={{ marginBottom: 6 }}>
                        Current Month Transport ({selectedStudentCurrentMonthTransportFee?.monthLabel || currentMonthKey}):{" "}
                        <strong>{money(selectedStudentCurrentMonthTransportFee?.amount || 0)}</strong>
                      </p>
                      <p style={{ marginTop: 0 }}>
                        Status:{" "}
                        <span className={selectedStudentCurrentMonthTransportFee?.status === "paid" ? "status-pill paid" : "status-pill due"}>
                          {selectedStudentCurrentMonthTransportFee?.status === "paid" ? "Paid" : "Pending"}
                        </span>
                      </p>
                      {selectedStudentCurrentMonthTransportFee?.status === "paid" ? (
                        <button className="table-btn" type="button" disabled>Transport Paid</button>
                      ) : (
                        <button
                          className="table-btn"
                          type="button"
                          onClick={() => copySchoolUpiId("Transport UPI ID")}
                          disabled={!selectedStudentCurrentMonthTransportFee}
                        >
                          Copy Transport UPI ID
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === "fees" && selectedStudent && (
            <section>
              <h1>Monthly Fee Table</h1>
              <div className="fees-overview-grid">
                <article className="fees-overview-card">
                  <p>Pending Months</p>
                  <h3>{selectedStudentPendingMonths.length}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>Approved Proofs</p>
                  <h3>{selectedStudentProofCounts.approved || 0}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>{proofForm.feeType === "transport" ? "Current Month Transport" : "Current Month Tuition"}</p>
                  <h3>{money(proofForm.feeType === "transport" ? (selectedStudentCurrentMonthTransportFee?.amount || 0) : (selectedStudentCurrentMonthFee?.amount || 0))}</h3>
                </article>
              </div>

              <div className="fees-segmented" role="tablist" aria-label="Fee workflow sections">
                <button className={feesView === "submit" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setFeesView("submit")}>1. Submit Proof</button>
                <button className={feesView === "proofs" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setFeesView("proofs")}>2. Submitted Proofs</button>
                <button className={feesView === "months" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setFeesView("months")}>3. Monthly Table</button>
              </div>

              {feesView === "submit" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submit Payment Proof</h3>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Copy the school UPI ID, pay in any app, then upload the screenshot below.
                  </p>
                  <div className="upi-copy-panel">
                    <div className="upi-copy-eyebrow">School Payment Account</div>
                    <div className="upi-copy-title-block">
                      <strong>{SCHOOL_PAYEE}</strong>
                      <span>Copy the UPI ID and pay in any UPI app, then upload the screenshot below.</span>
                    </div>
                    <div className="upi-copy-inline">
                      <div className="upi-copy-inline-text">
                        <span className="upi-copy-label">UPI ID</span>
                        <div className="upi-copy-inline-value">{SCHOOL_UPI_ID}</div>
                      </div>
                      <button type="button" className="upi-copy-btn" onClick={() => copySchoolUpiId("School UPI ID")}>
                        Copy
                      </button>
                    </div>
                  </div>
                  <form className="admin-form" onSubmit={submitProof}>
                    <div className="form-field">
                      <label>Fee Type *</label>
                      <select
                        value={proofForm.feeType}
                        onChange={(e) => setProofForm((p) => ({ ...p, feeType: e.target.value, monthKey: "" }))}
                      >
                        <option value="tuition">Tuition</option>
                        <option value="transport" disabled={!selectedStudent.transport?.enabled}>Transport</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Month *</label>
                      <select value={proofForm.monthKey} onChange={(e) => setProofForm((p) => ({ ...p, monthKey: e.target.value }))} required>
                        <option value="">Select Month</option>
                        {selectedStudentPendingMonths.map((month) => (
                          <option key={month.monthKey} value={month.monthKey}>
                            {month.monthLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Months To Cover *</label>
                      <select
                        value={proofForm.monthsToCover}
                        onChange={(e) => setProofForm((p) => ({ ...p, monthsToCover: Number(e.target.value) || 1 }))}
                      >
                        {Array.from({ length: 12 }, (_, idx) => idx + 1).map((count) => (
                          <option key={count} value={count}>{count} Month{count > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>UPI Transaction ID (Optional)</label>
                      <input value={proofForm.txnId} onChange={(e) => setProofForm((p) => ({ ...p, txnId: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label>Coverage</label>
                      <span className="dash-subtitle" style={{ padding: 0 }}>
                        {proofCoverageRangeText}
                      </span>
                    </div>
                    <div className="form-field">
                      <label>Expected Amount</label>
                      <span className="dash-subtitle" style={{ padding: 0 }}>{money(proofExpectedAmountWithAdvance)}</span>
                    </div>
                    <div className="form-field">
                      <label>Screenshot *</label>
                      <input type="file" accept="image/*" onChange={handleProofScreenshot} />
                    </div>
                    <div className="form-field">
                      <label>Preview</label>
                      {proofForm.screenshotUrl ? (
                        <img src={proofForm.screenshotUrl} alt="Payment proof" className="profile-avatar" />
                      ) : (
                        <span className="dash-subtitle" style={{ padding: 0 }}>No screenshot selected</span>
                      )}
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={proofSubmitting}>
                        {proofSubmitting ? "Submitting..." : "Submit Proof"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {feesView === "proofs" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submitted Proofs</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Coverage</th>
                          <th>Txn ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudentProofs.length === 0 && <tr><td colSpan={7}>No proofs submitted yet.</td></tr>}
                        {selectedStudentProofs.map((proof) => (
                          <tr key={proof.id}>
                            <td>{new Date(proof.createdAt).toLocaleDateString()}</td>
                            <td>{proof.feeType === "transport" ? "Transport" : proof.feeType === "annual" ? "Annual" : proof.feeType === "admission" ? "Admission" : "Tuition"}</td>
                            <td>
                              {(proof.coverageStartKey || proof.monthKey) === (proof.coverageEndKey || proof.monthKey)
                                ? (proof.coverageStartKey || proof.monthKey)
                                : `${proof.coverageStartKey || proof.monthKey} to ${proof.coverageEndKey || proof.monthKey}`}
                            </td>
                            <td>{proof.txnId || "-"}</td>
                            <td>{money(proof.amount)}</td>
                            <td>
                              <span className={proof.status === "approved" ? "status-pill paid" : proof.status === "rejected" ? "status-pill due" : "status-pill pending"}>
                                {proof.status}
                              </span>
                            </td>
                            <td>
                              {proof.status === "rejected" ? (
                                <button className="table-btn" type="button" onClick={() => startResubmit(proof)}>Resubmit</button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {feesView === "months" && (
                <div className="dash-panel">
                  <h3>{selectedStudent.name} ({selectedStudent.academicYear || "Academic Year"}) - {proofForm.feeType === "transport" ? "Transport" : "Tuition"}</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table fees-months-table">
                      <thead><tr><th>Month</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {selectedStudentMonths.map((month) => (
                          <tr key={month.monthKey}>
                            <td data-label="Month">{month.monthLabel}</td>
                            <td data-label="Amount">{money(month.amount)}</td>
                            <td data-label="Due Date">{month.dueDate ? new Date(month.dueDate).toLocaleDateString() : "-"}</td>
                            <td data-label="Status">
                              <span className={month.status === "paid" ? "status-pill paid" : "status-pill due"}>
                                {month.status === "paid" ? "Paid" : "Pending"}
                              </span>
                            </td>
                            <td data-label="Action">
                              {month.status === "paid" ? (
                                <span>Completed</span>
                              ) : (
                                <button className="table-btn" type="button" onClick={() => copySchoolUpiId("School UPI ID")}>
                                  Copy Payment UPI
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeTab === "annual-fee" && selectedStudent && (
            <section>
              <h1>Annual Fee</h1>
              <div className="fees-overview-grid">
                <article className="fees-overview-card">
                  <p>Academic Year</p>
                  <h3>{selectedStudentAnnualFee?.monthLabel || selectedStudent.academicYear || "-"}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>Annual Amount</p>
                  <h3>{money(selectedStudentAnnualFee?.amount || 0)}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>Approved Proofs</p>
                  <h3>{selectedStudentAnnualProofCounts.approved || 0}</h3>
                </article>
              </div>

              <div className="fees-segmented" role="tablist" aria-label="Annual fee workflow sections">
                <button className={annualView === "submit" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAnnualView("submit")}>1. Submit Proof</button>
                <button className={annualView === "proofs" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAnnualView("proofs")}>2. Submitted Proofs</button>
                <button className={annualView === "summary" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAnnualView("summary")}>3. Annual Details</button>
              </div>

              {annualView === "submit" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submit Annual Payment Proof</h3>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Copy the school UPI ID, pay the annual amount in any app, then upload the screenshot below.
                  </p>
                  <div className="upi-copy-panel">
                    <div className="upi-copy-eyebrow">School Payment Account</div>
                    <div className="upi-copy-title-block">
                      <strong>{SCHOOL_PAYEE}</strong>
                      <span>Pay the annual fee manually, then upload the payment screenshot for approval.</span>
                    </div>
                    <div className="upi-copy-inline">
                      <div className="upi-copy-inline-text">
                        <span className="upi-copy-label">UPI ID</span>
                        <div className="upi-copy-inline-value">{SCHOOL_UPI_ID}</div>
                      </div>
                      <button type="button" className="upi-copy-btn" onClick={() => copySchoolUpiId("School UPI ID")}>
                        Copy
                      </button>
                    </div>
                  </div>
                  <form className="admin-form" onSubmit={submitProof}>
                    <div className="form-field">
                      <label>Academic Year *</label>
                      <input value={selectedStudentAnnualFee?.monthLabel || selectedStudent.academicYear || "-"} readOnly />
                    </div>
                    <div className="form-field">
                      <label>Annual Amount</label>
                      <input value={money(selectedStudentAnnualFee?.amount || 0)} readOnly />
                    </div>
                    <div className="form-field">
                      <label>UPI Transaction ID (Optional)</label>
                      <input value={proofForm.txnId} onChange={(e) => setProofForm((p) => ({ ...p, feeType: "annual", monthKey: selectedStudentAnnualFee?.monthKey || "", txnId: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label>Status</label>
                      <span className="dash-subtitle" style={{ padding: 0 }}>{selectedStudentAnnualFee?.status === "paid" ? "Already Paid" : "Pending Approval"}</span>
                    </div>
                    <div className="form-field">
                      <label>Screenshot *</label>
                      <input type="file" accept="image/*" onChange={handleProofScreenshot} />
                    </div>
                    <div className="form-field">
                      <label>Preview</label>
                      {proofForm.screenshotUrl ? (
                        <img src={proofForm.screenshotUrl} alt="Annual payment proof" className="profile-avatar" />
                      ) : (
                        <span className="dash-subtitle" style={{ padding: 0 }}>No screenshot selected</span>
                      )}
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={proofSubmitting || selectedStudentAnnualFee?.status === "paid" || !selectedStudentAnnualFee}>
                        {proofSubmitting ? "Submitting..." : selectedStudentAnnualFee?.status === "paid" ? "Annual Fee Paid" : "Submit Annual Proof"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {annualView === "proofs" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submitted Annual Proofs</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Academic Year</th>
                          <th>Txn ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudentAnnualProofs.length === 0 && <tr><td colSpan={7}>No annual proofs submitted yet.</td></tr>}
                        {selectedStudentAnnualProofs.map((proof) => (
                          <tr key={proof.id}>
                            <td>{new Date(proof.createdAt).toLocaleDateString()}</td>
                            <td>Annual</td>
                            <td>{proof.monthKey || selectedStudent.academicYear || "-"}</td>
                            <td>{proof.txnId || "-"}</td>
                            <td>{money(proof.amount)}</td>
                            <td>
                              <span className={proof.status === "approved" ? "status-pill paid" : proof.status === "rejected" ? "status-pill due" : "status-pill pending"}>
                                {proof.status}
                              </span>
                            </td>
                            <td>
                              {proof.status === "rejected" ? (
                                <button className="table-btn" type="button" onClick={() => startResubmit(proof)}>Resubmit</button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {annualView === "summary" && (
                <div className="dash-panel">
                  <h3>{selectedStudent.name} - Annual Fee Summary</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table fees-months-table">
                      <thead><tr><th>Academic Year</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {!selectedStudentAnnualFee && <tr><td colSpan={5}>No annual fee configured.</td></tr>}
                        {selectedStudentAnnualFee && (
                          <tr>
                            <td data-label="Academic Year">{selectedStudentAnnualFee.monthLabel}</td>
                            <td data-label="Amount">{money(selectedStudentAnnualFee.amount)}</td>
                            <td data-label="Due Date">{selectedStudentAnnualFee.dueDate ? new Date(selectedStudentAnnualFee.dueDate).toLocaleDateString() : "-"}</td>
                            <td data-label="Status">
                              <span className={selectedStudentAnnualFee.status === "paid" ? "status-pill paid" : "status-pill due"}>
                                {selectedStudentAnnualFee.status === "paid" ? "Paid" : "Pending"}
                              </span>
                            </td>
                            <td data-label="Action">
                              {selectedStudentAnnualFee.status === "paid" ? (
                                <span>Completed</span>
                              ) : (
                                <button className="table-btn" type="button" onClick={() => copySchoolUpiId("School UPI ID")}>
                                  Copy Payment UPI
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeTab === "admission-fee" && selectedStudent && (
            <section>
              <h1>Admission Fee</h1>
              <div className="fees-overview-grid">
                <article className="fees-overview-card">
                  <p>Academic Year</p>
                  <h3>{selectedStudentAdmissionFee?.monthLabel || selectedStudent.academicYear || "-"}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>Admission Amount</p>
                  <h3>{money(selectedStudentAdmissionFee?.amount || 0)}</h3>
                </article>
                <article className="fees-overview-card">
                  <p>Approved Proofs</p>
                  <h3>{selectedStudentAdmissionProofCounts.approved || 0}</h3>
                </article>
              </div>

              <div className="fees-segmented" role="tablist" aria-label="Admission fee workflow sections">
                <button className={admissionView === "submit" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAdmissionView("submit")}>1. Submit Proof</button>
                <button className={admissionView === "proofs" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAdmissionView("proofs")}>2. Submitted Proofs</button>
                <button className={admissionView === "summary" ? "fees-segment-btn active" : "fees-segment-btn"} type="button" onClick={() => setAdmissionView("summary")}>3. Admission Details</button>
              </div>

              {admissionView === "submit" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submit Admission Payment Proof</h3>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Copy the school UPI ID, pay the admission amount in any app, then upload the screenshot below.
                  </p>
                  <div className="upi-copy-panel">
                    <div className="upi-copy-eyebrow">School Payment Account</div>
                    <div className="upi-copy-title-block">
                      <strong>{SCHOOL_PAYEE}</strong>
                      <span>Pay the admission fee manually, then upload the payment screenshot for approval.</span>
                    </div>
                    <div className="upi-copy-inline">
                      <div className="upi-copy-inline-text">
                        <span className="upi-copy-label">UPI ID</span>
                        <div className="upi-copy-inline-value">{SCHOOL_UPI_ID}</div>
                      </div>
                      <button type="button" className="upi-copy-btn" onClick={() => copySchoolUpiId("School UPI ID")}>
                        Copy
                      </button>
                    </div>
                  </div>
                  <form className="admin-form" onSubmit={submitProof}>
                    <div className="form-field">
                      <label>Academic Year *</label>
                      <input value={selectedStudentAdmissionFee?.monthLabel || selectedStudent.academicYear || "-"} readOnly />
                    </div>
                    <div className="form-field">
                      <label>Admission Amount</label>
                      <input value={money(selectedStudentAdmissionFee?.amount || 0)} readOnly />
                    </div>
                    <div className="form-field">
                      <label>UPI Transaction ID (Optional)</label>
                      <input value={proofForm.txnId} onChange={(e) => setProofForm((p) => ({ ...p, feeType: "admission", monthKey: selectedStudentAdmissionFee?.monthKey || "", txnId: e.target.value }))} />
                    </div>
                    <div className="form-field">
                      <label>Status</label>
                      <span className="dash-subtitle" style={{ padding: 0 }}>{selectedStudentAdmissionFee?.status === "paid" ? "Already Paid" : "Pending Approval"}</span>
                    </div>
                    <div className="form-field">
                      <label>Screenshot *</label>
                      <input type="file" accept="image/*" onChange={handleProofScreenshot} />
                    </div>
                    <div className="form-field">
                      <label>Preview</label>
                      {proofForm.screenshotUrl ? (
                        <img src={proofForm.screenshotUrl} alt="Admission payment proof" className="profile-avatar" />
                      ) : (
                        <span className="dash-subtitle" style={{ padding: 0 }}>No screenshot selected</span>
                      )}
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={proofSubmitting || selectedStudentAdmissionFee?.status === "paid" || !selectedStudentAdmissionFee}>
                        {proofSubmitting ? "Submitting..." : selectedStudentAdmissionFee?.status === "paid" ? "Admission Fee Paid" : "Submit Admission Proof"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {admissionView === "proofs" && (
                <div className="dash-panel" style={{ marginBottom: 12 }}>
                  <h3>Submitted Admission Proofs</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Academic Year</th>
                          <th>Txn ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudentAdmissionProofs.length === 0 && <tr><td colSpan={7}>No admission proofs submitted yet.</td></tr>}
                        {selectedStudentAdmissionProofs.map((proof) => (
                          <tr key={proof.id}>
                            <td>{new Date(proof.createdAt).toLocaleDateString()}</td>
                            <td>Admission</td>
                            <td>{proof.monthKey || selectedStudent.academicYear || "-"}</td>
                            <td>{proof.txnId || "-"}</td>
                            <td>{money(proof.amount)}</td>
                            <td>
                              <span className={proof.status === "approved" ? "status-pill paid" : proof.status === "rejected" ? "status-pill due" : "status-pill pending"}>
                                {proof.status}
                              </span>
                            </td>
                            <td>
                              {proof.status === "rejected" ? (
                                <button className="table-btn" type="button" onClick={() => startResubmit(proof)}>Resubmit</button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {admissionView === "summary" && (
                <div className="dash-panel">
                  <h3>{selectedStudent.name} - Admission Fee Summary</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table fees-months-table">
                      <thead><tr><th>Academic Year</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {!selectedStudentAdmissionFee && <tr><td colSpan={5}>No admission fee configured.</td></tr>}
                        {selectedStudentAdmissionFee && (
                          <tr>
                            <td data-label="Academic Year">{selectedStudentAdmissionFee.monthLabel}</td>
                            <td data-label="Amount">{money(selectedStudentAdmissionFee.amount)}</td>
                            <td data-label="Due Date">{selectedStudentAdmissionFee.dueDate ? new Date(selectedStudentAdmissionFee.dueDate).toLocaleDateString() : "-"}</td>
                            <td data-label="Status">
                              <span className={selectedStudentAdmissionFee.status === "paid" ? "status-pill paid" : "status-pill due"}>
                                {selectedStudentAdmissionFee.status === "paid" ? "Paid" : "Pending"}
                              </span>
                            </td>
                            <td data-label="Action">
                              {selectedStudentAdmissionFee.status === "paid" ? (
                                <span>Completed</span>
                              ) : (
                                <button className="table-btn" type="button" onClick={() => copySchoolUpiId("School UPI ID")}>
                                  Copy Payment UPI
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeTab === "history" && (
            <section>
              <h1>Payment History</h1>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead><tr><th>Date</th><th>Child</th><th>Type</th><th>Month</th><th>Amount</th><th>Status</th><th>Method</th><th>Reference</th></tr></thead>
                  <tbody>
                    {transactions.length === 0 && <tr><td colSpan={8}>No transactions yet.</td></tr>}
                    {transactions.map((tx) => (
                      <tr key={tx._id}>
                        <td>{new Date(tx.createdAt).toLocaleString()}</td>
                        <td>{tx.student?.name || "NA"}</td>
                        <td>{tx.feeType === "transport" ? "Transport" : tx.feeType === "annual" ? "Annual" : tx.feeType === "admission" ? "Admission" : "Tuition"}</td>
                        <td>{tx.monthKey || tx.academicYear || "-"}</td>
                        <td>{money(tx.amount)}</td>
                        <td>{tx.status}</td>
                        <td>{tx.paymentMethod || "UPI"}</td>
                        <td>{tx.txnId || tx.razorpay_payment_id || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "homework" && (
            <section>
              <h1>Homework {selectedStudent ? `- ${selectedStudent.name}` : ""}</h1>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead><tr><th>Date</th><th>Title</th><th>Class</th><th>Subject</th><th>Due Date</th><th>File</th><th>Students</th></tr></thead>
                  <tbody>
                    {selectedStudentHomework.length === 0 && <tr><td colSpan={7}>No homework found for selected student.</td></tr>}
                    {selectedStudentHomework.map((item) => (
                      <tr key={item.id}>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td>{item.title}</td>
                        <td>{item.className} - {item.section || "NA"}</td>
                        <td>{item.subject || "-"}</td>
                        <td>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "-"}</td>
                        <td>{item.attachmentUrl ? <a href={item.attachmentUrl} download={item.attachmentName || "homework-file"} target="_blank" rel="noreferrer">{item.attachmentName || "Download"}</a> : "-"}</td>
                        <td>{(item.linkedStudents || []).map((s) => s.name).join(", ") || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "homework-submit" && selectedStudent && (
            <section>
              <h1>Submit Homework - {selectedStudent.name}</h1>
              <div className="dash-panel" style={{ marginBottom: 12 }}>
                <form className="admin-form" onSubmit={submitHomework}>
                  <div className="form-field">
                    <label>Homework *</label>
                    <select
                      value={homeworkSubmitForm.homeworkId}
                      onChange={(e) => setHomeworkSubmitForm((prev) => ({ ...prev, homeworkId: e.target.value }))}
                      required
                    >
                      <option value="">Select Homework</option>
                      {selectedStudentHomework.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title} ({item.subject || "General"}) {item.dueDate ? `| Due ${new Date(item.dueDate).toLocaleDateString()}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Note</label>
                    <input
                      value={homeworkSubmitForm.note}
                      onChange={(e) => setHomeworkSubmitForm((prev) => ({ ...prev, note: e.target.value }))}
                      placeholder="Optional note for teacher"
                    />
                  </div>
                  <div className="form-field">
                    <label>Attachment (Image/PDF)</label>
                    <input type="file" accept="image/*,.pdf,application/pdf" onChange={handleHomeworkAttachment} />
                  </div>
                  <div className="form-field">
                    <label>Preview</label>
                    {homeworkSubmitForm.attachmentUrl ? (
                      homeworkSubmitForm.attachmentUrl.startsWith("data:image") ? (
                        <img src={homeworkSubmitForm.attachmentUrl} alt="Homework attachment preview" className="profile-avatar" />
                      ) : (
                        <a href={homeworkSubmitForm.attachmentUrl} target="_blank" rel="noreferrer">View attached PDF</a>
                      )
                    ) : (
                      <span className="dash-subtitle" style={{ padding: 0 }}>No file selected</span>
                    )}
                  </div>
                  <div className="admin-form-actions">
                    <button type="submit" disabled={submittingHomework}>
                      {submittingHomework ? "Submitting..." : "Submit Homework"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="dash-panel">
                <h3>Submission History</h3>
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead><tr><th>Date</th><th>Homework</th><th>Status</th><th>Attachment</th><th>Teacher Comment</th></tr></thead>
                    <tbody>
                      {selectedStudentSubmissions.length === 0 && <tr><td colSpan={5}>No submissions yet.</td></tr>}
                      {selectedStudentSubmissions.map((row) => (
                        <tr key={row._id}>
                          <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                          <td>{row.homework?.title || "-"}</td>
                          <td>
                            <span className={row.status === "reviewed" ? "status-pill paid" : row.status === "late" ? "status-pill due" : "status-pill pending"}>
                              {row.status}
                            </span>
                          </td>
                          <td>{row.attachmentUrl ? <a href={row.attachmentUrl} target="_blank" rel="noreferrer">View</a> : "-"}</td>
                          <td>{row.reviewComment || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {activeTab === "timetable" && selectedStudent && (
            <section>
              <h1>Timetable - {selectedStudent.name}</h1>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead><tr><th>Day</th><th>Period</th><th>Time</th><th>Subject</th><th>Teacher</th><th>Room</th></tr></thead>
                  <tbody>
                    {timetable.length === 0 && <tr><td colSpan={6}>No timetable entries found.</td></tr>}
                    {timetable.map((row) => (
                      <tr key={row._id}>
                        <td>{row.dayOfWeek}</td>
                        <td>{row.period}</td>
                        <td>{row.startTime || "-"} {row.endTime ? `- ${row.endTime}` : ""}</td>
                        <td>{row.subject || "-"}</td>
                        <td>{row.teacherName || "-"}</td>
                        <td>{row.room || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "notices" && (
            <section>
              <h1>Notices</h1>
              <div className="notice-feed">
                {notices.length === 0 && <p>No notices yet.</p>}
                {notices.map((notice) => (
                  <article key={notice.id} className="notice-feed-item">
                    <h3>{notice.title}</h3>
                    <p>{notice.details || "-"}</p>
                    <small>
                      {new Date(notice.noticeDate || notice.createdAt).toLocaleDateString()}
                      {toMs(notice.createdAt) > (seenMeta.notices || 0) && (
                        <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>
                      )}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === "events" && (
            <section>
              <h1>Events</h1>
              <div className="notice-feed">
                {events.length === 0 && <p>No events yet.</p>}
                {events.map((event) => (
                  <article key={event.id} className="notice-feed-item event-feed-item">
                    <h3>{event.title}</h3>
                    <p>{event.description || "-"}</p>
                    <small>
                      Event Date: {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : "-"}
                      {toMs(event.createdAt) > (seenMeta.events || 0) && (
                        <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>
                      )}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === "report-card" && (
            <section>
              <h1>Report Card {selectedStudent ? `- ${selectedStudent.name}` : ""}</h1>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Exam</th>
                      <th>Subject</th>
                      <th>Date</th>
                      <th>Marks</th>
                      <th>Max</th>
                      <th>%</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportCardLoading && <tr><td colSpan={7}>Loading report card...</td></tr>}
                    {!reportCardLoading && reportCardRows.length === 0 && <tr><td colSpan={7}>No report card records found.</td></tr>}
                    {!reportCardLoading && reportCardRows.map((row) => (
                      <tr key={row.exam?.id || `${row.exam?.title}-${row.exam?.examDate}`}>
                        <td>{row.exam?.title || "-"}</td>
                        <td>{row.exam?.subject || "-"}</td>
                        <td>{row.exam?.examDate ? new Date(row.exam.examDate).toLocaleDateString() : "-"}</td>
                        <td>{row.marks ?? "-"}</td>
                        <td>{row.exam?.maxMarks || "-"}</td>
                        <td>{row.percentage ?? "-"}</td>
                        <td>{row.grade || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "hall-ticket" && selectedStudent && (
            <section>
              <h1>Exam Timetable & Hall Ticket - {selectedStudent.name}</h1>
              <div className="dash-panel" style={{ marginBottom: 12 }}>
                <h3>Exam Timetable</h3>
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead><tr><th>Exam</th><th>Subject</th><th>Date</th><th>Max Marks</th></tr></thead>
                    <tbody>
                      {examTimetable.length === 0 && <tr><td colSpan={4}>No exam timetable published yet.</td></tr>}
                      {examTimetable.map((row) => (
                        <tr key={row.id}>
                          <td>{row.title}</td>
                          <td>{row.subject || "-"}</td>
                          <td>{row.examDate ? new Date(row.examDate).toLocaleDateString() : "-"}</td>
                          <td>{row.maxMarks || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="dash-panel">
                <h3>Hall Tickets</h3>
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead><tr><th>Exam</th><th>Date</th><th>Seat</th><th>Room</th><th>Center</th><th>Instructions</th><th>Action</th></tr></thead>
                    <tbody>
                      {selectedStudentHallTickets.length === 0 && <tr><td colSpan={7}>No hall tickets available yet.</td></tr>}
                      {selectedStudentHallTickets.map((row) => (
                        <tr key={row.id}>
                          <td>{row.exam?.title || "-"}</td>
                          <td>{row.exam?.examDate ? new Date(row.exam.examDate).toLocaleDateString() : "-"}</td>
                          <td>{row.seatNumber || "-"}</td>
                          <td>{row.room || "-"}</td>
                          <td>{row.centerName || "-"}</td>
                          <td>{row.instructions || "-"}</td>
                          <td>
                            <div className="icon-actions">
                              <button type="button" className="table-btn" onClick={() => openHallTicketDocument(row, false)}>Print View</button>
                              <button type="button" className="table-btn" onClick={() => openHallTicketDocument(row, true)}>Download PDF</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {activeTab === "notifications" && (
            <section>
              <div className="table-header-row">
                <h1 style={{ margin: 0 }}>Notifications</h1>
                <button className="table-btn" type="button" onClick={markNotificationsRead} disabled={markingNotificationRead || notificationUnreadCount === 0}>
                  {markingNotificationRead ? "Updating..." : "Mark All Read"}
                </button>
              </div>
              <div className="notice-feed">
                {notifications.length === 0 && <p>No notifications yet.</p>}
                {notifications.map((item) => (
                  <article key={item.id} className="notice-feed-item">
                    <h3>{item.title}</h3>
                    <p>{item.message || "-"}</p>
                    <small>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      {item.unread && <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>
      </section>
      <nav className="mobile-only mobile-bottom-nav" aria-label="Quick navigation">
        <button type="button" className={activeTab === "overview" ? "mobile-bottom-btn active" : "mobile-bottom-btn"} onClick={() => setTabAndCloseMenu("overview")}>
          <AppIcon name="dashboard" />
          <span>Home</span>
        </button>
        <button type="button" className={activeTab === "fees" || activeTab === "history" ? "mobile-bottom-btn active" : "mobile-bottom-btn"} onClick={() => setTabAndCloseMenu("fees")}>
          <AppIcon name="fee" />
          <span>Fees</span>
        </button>
        <button type="button" className={activeTab === "homework" || activeTab === "homework-submit" ? "mobile-bottom-btn active" : "mobile-bottom-btn"} onClick={() => setTabAndCloseMenu("homework")}>
          <AppIcon name="assignment" />
          <span>Homework</span>
        </button>
        <button type="button" className={activeTab === "notifications" || activeTab === "notices" ? "mobile-bottom-btn active" : "mobile-bottom-btn"} onClick={() => setTabAndCloseMenu("notifications")}>
          <AppIcon name="notice" />
          <span>Alerts</span>
        </button>
        <button type="button" className={mobileNavOpen ? "mobile-bottom-btn active" : "mobile-bottom-btn"} onClick={() => setMobileNavOpen((prev) => !prev)}>
          <AppIcon name="list" />
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
};

export default ParentDashboard;
